import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Cart apps (ÉLITE membership, Seel) add and remove their own line items with
 * their own requests. The theme never hears about those, so the drawer keeps
 * rendering stale line items and totals.
 *
 * Watch for cart mutations the theme did not make and dispatch `cart:update`
 * without sections, which makes cart-items-component re-render the section.
 * `queueCartChange` holds the theme's own changes until those requests settle.
 */

const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;

const SOURCE = 'cart-app-sync';

/** @typedef {{items: Array<{handle: string, key: string, quantity: number, variant_id: number, vendor?: string, product_type?: string}>, item_count: number, sections?: Record<string, string>}} Cart */

/** Cart endpoints whose response body is the whole cart (add.js returns only the added items). */
const RETURNS_CART = /\/cart\/(change|update|clear)(\.js)?$/;

/**
 * @param {RequestInfo | URL} input
 * @returns {string}
 */
function urlOf(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input?.url ?? '';
}

export const SINGLE_UNIT_HANDLES = ['membership', 'worry-free-purchase'];

/**
 * Seel's protection line, which the cart shows only in the total; matches snippets/cart-line-is-seel.liquid.
 * @param {Cart['items'][number]} item
 */
export const isSeelLine = (item) => item.vendor === 'Seel' || item.product_type === 'SEEL-WFP';

/**
 * Item count shown to shoppers, without Seel's line; matches snippets/cart-visible-item-count.liquid.
 * @param {Cart} cart
 */
export const visibleItemCount = (cart) =>
  cart.items.reduce((count, item) => (isSeelLine(item) ? count : count + item.quantity), 0);

const originalFetch = window.fetch;

/** @returns {Promise<Cart>} the current cart */
function getCart() {
  return originalFetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
}

/**
 * Limit each single-unit add-on to one per cart.
 * @param {Cart} cart
 * @returns {Promise<Cart>} the cart after any correction
 */
async function enforceSingleUnitAddons(cart) {
  const changes = SINGLE_UNIT_HANDLES.flatMap((handle) => {
    const lines = cart.items.filter((item) => item.handle === handle);
    if (lines.reduce((total, item) => total + item.quantity, 0) <= 1) return [];

    return lines.map((item, index) => ({ id: item.key, quantity: index === 0 ? 1 : 0 }));
  });

  if (changes.length === 0) return cart;

  for (const change of changes) {
    await originalFetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(change),
    });
  }

  return getCart();
}

/** Quiet period after a cart request before the cart counts as settled. */
const QUIET_MS = 150;
/** Longest a queued change waits for cart apps, so a hung request can't freeze the cart. */
const MAX_WAIT_MS = 10000;

/** @type {ReturnType<typeof setTimeout> | null} */
let pending = null;
let reportedByTheme = false;
let inFlight = 0;
/** @type {ReturnType<typeof setTimeout> | null} */
let readGrace = null;
/** @type {Cart | null} */
let settledCart = null;
/** @type {Array<(cart: Cart | null) => void>} */
let settledWaiters = [];
/** Cart from the last mutation's response, so the follow-up refresh can skip re-reading /cart.js. @type {Cart | null} */
let respondedCart = null;
let mutationCount = 0;

/** Seel fix: whether a cart request or its follow-up refresh is still running. */
const isBusy = () => inFlight > 0 || pending !== null || readGrace !== null;

/** Seel fix: releases the changes waiting in `cartSettled` once the cart is quiet. */
function flushSettled() {
  if (isBusy()) return;
  settledWaiters.forEach((resolve) => resolve(settledCart));
  settledWaiters = [];
}

/**
 * Seel fix: resolves once no cart request is in flight and the follow-up refresh has run.
 * @returns {Promise<Cart | null>} the cart as of the last change, or null when nothing has changed since load
 */
function cartSettled() {
  if (!isBusy()) return Promise.resolve(settledCart);

  return new Promise((resolve) => {
    settledWaiters.push(resolve);

    // On giving up, read the cart afresh: a key from before a cart app's change no longer exists.
    setTimeout(() => {
      if (!settledWaiters.includes(resolve)) return;
      console.warn(SOURCE, `cart apps still busy after ${MAX_WAIT_MS}ms, sending the change anyway`);
      getCart().then(resolve, () => resolve(settledCart));
    }, MAX_WAIT_MS);
  });
}

let queue = Promise.resolve();
let queued = 0;

/**
 * Seel fix: flags the page while changes wait behind Seel, so the cart counts shimmer.
 * @param {number} delta
 */
function trackQueued(delta) {
  queued += delta;
  // Shimmers the cart counts (base.css); on <html> because Seel re-renders the header and drawer.
  document.documentElement.toggleAttribute('data-cart-pending', queued > 0);
}

/**
 * Seel fix: runs a theme cart change after earlier ones, once Seel has stopped changing the cart.
 * @template T
 * @param {(cart: Cart | null) => Promise<T>} task - Gets the cart as of the last change.
 * @returns {Promise<T>}
 */
export function queueCartChange(task) {
  trackQueued(1);

  const run = queue.then(cartSettled).then(task);
  queue = run.catch(() => {});
  run.catch(() => {}).finally(() => trackQueued(-1));

  return run;
}

/**
 * Seel fix: counts a request as in flight until it and its follow-up have run.
 * @param {Promise<Response>} result
 * @param {(response: Response) => unknown} after
 */
function track(result, after) {
  inFlight++;
  result
    .then(after)
    .catch(() => {})
    .finally(() => {
      inFlight--;
      flushSettled();
    });
}

function announce(delay = QUIET_MS) {
  clearTimeout(pending);
  const timer = setTimeout(async () => {
    const alreadyReported = reportedByTheme;
    reportedByTheme = false;

    const responded = inFlight === 0 ? respondedCart : null;
    respondedCart = null;

    try {
      const cart = responded ?? (await getCart());
      const enforced = await enforceSingleUnitAddons(cart);
      settledCart = enforced;

      // Announce only unreported mutations or corrections.
      if (enforced === cart && alreadyReported) return;

      // Sections the app's own request rendered let the drawer morph without fetching itself again.
      const sections = enforced === cart ? cart.sections ?? undefined : undefined;
      document.dispatchEvent(
        new CartUpdateEvent(enforced, SOURCE, { itemCount: visibleItemCount(enforced), source: SOURCE, sections })
      );
    } catch (error) {
      console.error(SOURCE, error);
    } finally {
      if (pending === timer) pending = null;
      flushSettled();
    }
  }, delay);
  pending = timer;
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (/** @type {CustomEvent} */ (event).detail?.data?.source !== SOURCE) reportedByTheme = true;
});

window.fetch = function (input, init) {
  const result = originalFetch.call(this, input, init);
  const url = new URL(urlOf(input), window.location.origin);

  const isMutation = CART_MUTATION.test(url.pathname);

  // Seel reads the cart, fetches a quote, then writes the cart back, so a theme change landing in between is lost.
  if (!isMutation && (url.searchParams.get('src') === 'seel' || url.hostname === 'api.seel.com')) {
    track(result, () => {
      clearTimeout(readGrace);
      readGrace = setTimeout(() => {
        readGrace = null;
        flushSettled();
      }, QUIET_MS);
    });
    return result;
  }

  if (!isMutation) return result;

  /*
   * Cancel a pending refresh when the next mutation starts, then refresh
   * once after the chain settles. A theme report for an earlier, already
   * settled chain must not cover this one, so forget it here.
   */
  clearTimeout(pending);
  pending = null;
  settledCart = null;
  respondedCart = null;
  reportedByTheme = false;
  const returnsCart = RETURNS_CART.test(url.pathname);
  const mutation = ++mutationCount;
  track(result, async (response) => {
    if (!response.ok) return;
    const cart = returnsCart ? await response.clone().json().catch(() => null) : null;
    // A mutation that started while the body was read refreshes for both.
    if (mutation !== mutationCount) return;
    respondedCart = cart;
    announce();
  });

  return result;
};

/*
 * The ÉLITE row's own click handler (on `.ll__cart-membership-*`, not the
 * checkbox) adds the membership for any click inside the row, so "Learn More"
 * added it before navigating. Stop the click at the document in the capture
 * phase so that handler never sees it; the default navigation is untouched.
 */
document.addEventListener(
  'click',
  (event) => {
    const { target } = event;
    if (target instanceof Element && target.closest('.ll__cart-membership-ho4ed3 a[href]')) event.stopPropagation();
  },
  true
);
